import { useState, useEffect } from "react";
import { Button, ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import {
  Plus,
  Search,
  MoreVertical,
  Power,
  PowerOff,
  Edit2,
  Trash2,
  ChevronRight,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { SynonymGroup } from "@/models/synonym-group/synonymGroup";
import CreateSynonymGroupDialog from "./components/CreateSynonymGroupDialog";
import PageContent from "@/pages/PageContent";
import useSynonymGroups from "@/hooks/useSynonymGroups";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { DataTablePagination } from "@/components/data-table/data-table-pagination";
import { useNavigate } from "react-router-dom";
import AppRoutes from "@/constants/routes/AppRoutes";
import { logger } from "@/lib/logger";

const SynonymGroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const { useQueryPagination, create, remove, activate, deactivate } =
    useSynonymGroups();

  const [synonymGroups, setSynonymGroups] = useState<SynonymGroup[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<SynonymGroup | null>(null);

  const [paginationInfo, setPaginationInfo] = useState({
    pageNumber: 1,
    pageSize: 10,
    search: "",
    totalPages: 1,
    totalRecords: 0,
    hasMore: false,
  });

  const fetchedSynonymGroups = useQueryPagination({
    pageNumber: paginationInfo.pageNumber,
    pageSize: paginationInfo.pageSize,
    search: paginationInfo.search,
    sortBy: "createdAt",
    sortAscending: false,
  });

  useEffect(() => {
    if (fetchedSynonymGroups.data && fetchedSynonymGroups.isFetched) {
      setPaginationInfo((prev) => ({
        ...prev,
        totalPages: fetchedSynonymGroups.data.totalPages ?? prev.totalPages,
        totalRecords:
          fetchedSynonymGroups.data.totalRecords ?? prev.totalRecords,
        hasMore: fetchedSynonymGroups.data.hasMore ?? prev.hasMore,
      }));
      setSynonymGroups(fetchedSynonymGroups.data.data || []);
    }
  }, [fetchedSynonymGroups.data, fetchedSynonymGroups.isFetched]);

  const handleCreateGroup = async (canonicalTerm: string) => {
    try {
      await create.mutateAsync({
        canonicalTerm,
      });
      showSuccessToast("Din nye gruppe er oprettet", "Succes");
      setIsCreateDialogOpen(false);
    } catch (err: unknown) {
      logger.error("Failed to create synonym group", {
        operation: "createSynonymGroup",
        error: err,
      });

      // Try to extract a meaningful message regardless of error shape
      let msg = "";
      if (typeof err === "string") {
        msg = err;
      } else if (err instanceof Error) {
        msg = err.message;
      } else if (err && typeof err === "object" && "Message" in err) {
        msg = String((err as unknown as { Message: string }).Message);
      }

      // If the message looks like JSON, try to parse it to get Message/status
      if (msg?.trim().startsWith("{")) {
        try {
          const parsed = JSON.parse(msg);
          if (parsed?.Message) msg = String(parsed.Message);
          else if (parsed?.message) msg = String(parsed.message);
          else if (parsed?.title) msg = String(parsed.title);
        } catch {
          // ignore JSON parse errors
        }
      }

      if (msg.toLowerCase().includes("canonical term already exists")) {
        showErrorToast(
          "Dette kanoniske term findes allerede på en anden gruppe",
          "Fejl"
        );
      } else {
        showErrorToast("Der skete en fejl under oprettelse", "Fejl");
      }
    }
  };

  const handleToggleActive = async (group: SynonymGroup) => {
    try {
      let message = "";

      if (group.isActive) {
        await deactivate.mutateAsync({
          groupId: group.id,
          deactivateGroup: true,
          synonym: undefined,
        });
        message = "Din synonym gruppe er deaktiveret";
      } else {
        await activate.mutateAsync({
          groupId: group.id,
          activateGroup: true,
          synonym: undefined,
        });

        message = "Din synonym gruppe er aktiveret";
      }

      showSuccessToast(message, "Succes");
    } catch (error) {
      logger.error("Failed to change synonym group active state", {
        operation: "toggleSynonymGroupActive",
        error,
        synonymGroupId: group.id,
        activate: !group.isActive,
      });
      const message = group.isActive
        ? "Der skete en fejl under deaktivering af synonym gruppe"
        : "Der skete en fejl under aktivering af synonym gruppe";
      showErrorToast(message, "Fejl");
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;
    try {
      await remove.mutateAsync(groupToDelete.id);
      showSuccessToast("Gruppe slettet", "Succes");
    } catch (error) {
      logger.error("Failed to delete synonym group", {
        operation: "deleteSynonymGroup",
        error,
        synonymGroupId: groupToDelete.id,
      });
      showErrorToast("Der skete en fejl under sletning af gruppe", "Fejl");
    } finally {
      setIsDeleteDialogOpen(false);
      setGroupToDelete(null);
    }
  };

  return (
    <PageContent title={"Synonymer"}>
      <div className="flex flex-col h-full w-full space-y-2 p-4 xl:px-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">
              Synonymgrupper
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Administrer kanoniske termer og synonymer til din organisations
              hurtigsøgning
            </p>
          </div>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Opret gruppe
          </Button>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filtrer synonymgrupper..."
              value={paginationInfo.search}
              onChange={(e) =>
                setPaginationInfo((prev) => ({
                  ...prev,
                  search: e.target.value,
                }))
              }
              className="pl-10"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col space-y-2">
          {fetchedSynonymGroups.isPending ? (
            <div className="rounded-lg border border-border bg-card p-8 text-center">
              <p className="text-muted-foreground">
                Indlæser synonymgrupper...
              </p>
            </div>
          ) : synonymGroups.length === 0 ? (
            <div className="rounded-lg border border-border bg-card p-8 text-center">
              <p className="text-muted-foreground">
                {paginationInfo.search
                  ? "Ingen synonymgrupper matcher din søgning"
                  : "Ingen synonymgrupper endnu. Opret din første for at komme i gang."}
              </p>
            </div>
          ) : (
            <div
              id="content-container"
              className="flex-1 min-h-0 flex flex-col overflow-hidden gap-3"
            >
              <div
                id="list-container"
                className="flex-1 min-h-0 overflow-auto flex flex-col space-y-2"
              >
                {synonymGroups.map((group) => {
                  const activeSynonyms = group.synonyms.filter(
                    (s) => s.active
                  ).length;
                  const totalSynonyms = group.synonyms.length;

                  return (
                    <div
                      key={group.id}
                      className="group flex items-center justify-between rounded-lg border border-border bg-card p-4 transition-colors hover:bg-accent/50 cursor-pointer"
                    >
                      <button
                        onClick={() =>
                          navigate(AppRoutes.settingsSynonymDetail(group.id))
                        }
                        className="flex flex-1 items-center gap-4 text-left cursor-pointer"
                      >
                        <div className="flex h-2 w-2 items-center justify-center">
                          <div
                            className={`h-2 w-2 rounded-full ${
                              group.isActive
                                ? "bg-success"
                                : "bg-muted-foreground"
                            }`}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-mono text-sm font-medium text-foreground">
                              {group.canonicalTerm}
                            </h3>
                            <Badge
                              variant={group.isActive ? "default" : "secondary"}
                              className="text-xs"
                            >
                              {group.isActive ? "Aktiv" : "Inaktiv"}
                            </Badge>
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                            <span>
                              {activeSynonyms} / {totalSynonyms} aktive{" "}
                              {totalSynonyms === 1 ? "synonym" : "synonymer"}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                      </button>
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="ml-2 opacity-0 transition-opacity group-hover:opacity-100"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                AppRoutes.settingsSynonymDetail(group.id)
                              )
                            }
                          >
                            <Edit2 className="mr-2 h-4 w-4" />
                            Administrer synonymer
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleToggleActive(group)}
                          >
                            {group.isActive ? (
                              <>
                                <PowerOff className="mr-2 h-4 w-4" />
                                Deaktiver gruppe
                              </>
                            ) : (
                              <>
                                <Power className="mr-2 h-4 w-4" />
                                Aktiver gruppe
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setGroupToDelete(group);
                              setIsDeleteDialogOpen(true);
                            }}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Slet
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })}
              </div>
              <div className="shrink-0">
                <DataTablePagination
                  pageCount={paginationInfo.totalPages}
                  pageIndex={paginationInfo.pageNumber - 1}
                  pageSize={paginationInfo.pageSize}
                  setPageIndex={(pi) => {
                    setPaginationInfo((prev) => ({
                      ...prev,
                      pageNumber: pi + 1,
                    }));
                  }}
                  setPageSize={(ps) => {
                    setPaginationInfo((prev) => ({
                      ...prev,
                      pageSize: ps,
                    }));
                  }}
                  pageSizeOptions={[10, 20, 30, 40, 50]}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateSynonymGroupDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onCreateGroup={handleCreateGroup}
        disabled={create.isPending}
      />

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteGroup}
        title="Slet synonymgruppe"
        description={`Alle synonymer i "${groupToDelete?.canonicalTerm ?? ""}" bliver permanent fjernet. Handlingen kan ikke fortrydes.`}
        confirmText="Slet gruppe"
        variant="destructive"
      />
    </PageContent>
  );
};

export default SynonymGroupsPage;
